import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EntityListResp } from '@/types/home';
import BigNumber from 'bignumber.js';
import { AWAITING_RESPONSE, RESPONDED } from '@/config/home';
import { Avatar } from '@/components/avatar';
import { StatusTag } from '@/components/status-tag';
import { formatNumber, appPath } from '@web3/base-utils';
// import { formatTimeDiff } from '@/utils/day';
import { ArrowchevronRightIcon } from '@bfe/moly-icons';
import { useFm, useParams } from '@web3/base-hooks';
import { scTrack } from '@web3/base-tracking';
import { Event, Page } from '@/config/track-config';
import { useRouter } from 'next/router';
import { TabKey } from './types';
import { getBridgeActors } from '@/services/home';

import { BridgeStatusTag } from './bridge-status-tag';

import AwaitRankLogo from '@/assets/svg/home/await_rank_logo.svg';
import RespondedRankLogo from '@/assets/svg/home/responded_rank_logo.svg';

import styles from './index.module.scss';

export type EntityListProps = {
  loading: boolean;
  data: EntityListResp[];
};

export const PAGE_SIZE = 5;

export const useData = (props: EntityListProps) => {
  const { loading, data } = props;
  const [currentTab, setCurrentTab] = useState<TabKey>(TabKey.BRIDGE);
  const [pageNo, setPageNo] = useState<number>(1);
  const [listLength, setListLength] = useState<number>(0);
  const [tableList, setTableList] = useState([]);
  const [bridgeData, setBridgeData] = useState([]);
  const [bridgeLoading, setBridgeLoading] = useState<boolean>(true);
  const pageSize = PAGE_SIZE;
  const fm = useFm();
  const bridgeDataLenRef = useRef(0);

  // await resp data
  const awaitRespData = useMemo(() => {
    const sortData =
      data
        ?.filter((v) => AWAITING_RESPONSE.includes(v?.supportStatus))
        ?.sort((a, b) => {
          return new BigNumber(b?.unblocked).comparedTo(
            new BigNumber(a?.unblocked)
          );
        }) || [];
    return sortData?.map((v, index) => {
      return {
        ...v,
        index,
      };
    });
  }, [data]);
  // fully support data
  const fullySupportData = useMemo(() => {
    const sortData = data
      ?.filter((v) => RESPONDED.includes(v?.supportStatus))
      ?.sort((a, b) => {
        return new BigNumber(b?.received).comparedTo(
          new BigNumber(a?.received)
        );
      });
    return sortData?.map((v, index) => {
      return {
        ...v,
        index,
      };
    });
  }, [data]);

  const tabList = useMemo(() => {
    return [
      {
        key: TabKey.BRIDGE,
        label: `${fm('bridgeActors')} (${bridgeLoading ? '--' : bridgeDataLenRef.current})`,
      },
      {
        key: TabKey.AWAIT_RESPONSE,
        label: `${fm('awaitingResponse3')} (${loading ? '--' : awaitRespData.length})`,
      },
      {
        key: TabKey.FULLY_SUPPORTED,
        label: `${fm('responded')} (${loading ? '--' : fullySupportData.length})`,
      },
    ];
  }, [
    awaitRespData.length,
    bridgeLoading,
    fm,
    fullySupportData.length,
    loading,
  ]);

  // loading
  const finallyLoading = useMemo(() => {
    if (currentTab === TabKey.BRIDGE) {
      return bridgeLoading;
    }
    return loading;
  }, [bridgeLoading, currentTab, loading]);

  const handleChangeTab = (value: TabKey) => {
    if (value === TabKey.FULLY_SUPPORTED) {
      scTrack(Event.HackbountyUserClick, {
        page_name: Page.HackbountyHomePage,
        button_name: 'RespondedTab',
      });
    }
    setCurrentTab(value);
    setPageNo(1);
  };

  const onChangePageNo = (v: number) => {
    setPageNo(v);
  };

  const fetchBridegData = useCallback(async () => {
    try {
      setBridgeLoading(true);
      const { code, result } = await getBridgeActors({
        page: pageNo,
        pageSize,
      });
      if (code === 0 && result) {
        const list = (result?.dataList || []).map((v, index) => ({
          ...v,
          index,
        }));
        const num = result?.pageInfo?.totalNum || 0;
        setListLength(num);
        setBridgeData(list);
        bridgeDataLenRef.current = num;
      } else {
        setBridgeData([]);
        setListLength(0);
      }
      setBridgeLoading(false);
    } catch {
      setBridgeLoading(false);
      setBridgeData([]);
      setListLength(0);
    }
  }, [pageNo, pageSize]);

  useEffect(() => {
    // 获取 brideg actor数据
    if (currentTab === TabKey.BRIDGE) {
      fetchBridegData();
    }
  }, [currentTab, fetchBridegData]);

  useEffect(() => {
    let list = [];
    if (currentTab === TabKey.BRIDGE) {
      list = bridgeData;
    } else {
      const data =
        currentTab === TabKey.AWAIT_RESPONSE ? awaitRespData : fullySupportData;
      list = data?.slice((pageNo - 1) * pageSize, pageNo * pageSize) || [];
      setListLength(data.length);
    }
    setTableList(list);
  }, [
    awaitRespData,
    bridgeData,
    currentTab,
    fullySupportData,
    pageNo,
    pageSize,
  ]);

  return {
    tabList,
    currentTab,
    tableList,
    handleChangeTab,
    pageNo,
    pageSize,
    onChangePageNo,
    listLength,
    finallyLoading,
  };
};

export const useColumn = (props: { currentTab: TabKey; pageNo: number }) => {
  const { currentTab, pageNo } = props;
  const router = useRouter();
  const { locale } = useParams();
  const fm = useFm();

  const bridgeCols = useMemo(() => {
    return [
      {
        accessorKey: 'rank',
        header: fm('rank'),
        cell: ({ row }) => {
          const { index } = row?.original || {};
          const rankNum = (pageNo - 1) * PAGE_SIZE + index + 1;
          return (
            <div className={styles.rank}>
              <AwaitRankLogo />
              <span>{rankNum}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'entityName',
        header: fm('endPoint'),
        cell: ({ row }) => {
          const { entityName, entityLogoUrl } = row?.original || {};

          return (
            <div className={styles.endPoint}>
              <Avatar src={entityLogoUrl} size="24" name={entityName} />
              <div className={styles.name}>{entityName}</div>
            </div>
          );
        },
      },
      {
        accessorKey: 'totalInflowFunds',
        header: fm('totalInflow'),
        cell: ({ row }) => {
          const { totalInflowFunds } = row?.original || {};
          if (!totalInflowFunds) return '--';

          return `$${formatNumber(totalInflowFunds, {
            kmb: false,
            decimal: 0,
            noTrailZero: true,
            roundMode: 1,
          })}`;
        },
      },
      {
        accessorKey: 'untraceableFunds',
        header: fm('untraceableFunds'),
        cell: ({ row }) => {
          const { untraceableFunds } = row?.original || {};
          if (!untraceableFunds) return '--';

          return `$${formatNumber(untraceableFunds, {
            kmb: false,
            decimal: 0,
            noTrailZero: true,
            roundMode: 1,
          })}`;
        },
      },
      {
        accessorKey: 'traceableFunds',
        header: fm('traceableFunds'),
        cell: ({ row }) => {
          const { traceableFunds } = row?.original || {};
          if (!traceableFunds) return '--';

          return `$${formatNumber(traceableFunds, {
            kmb: false,
            decimal: 0,
            noTrailZero: true,
            roundMode: 1,
          })}`;
        },
      },
      {
        accessorKey: 'from',
        header: fm('fromTo'),
        cell: ({ row }) => {
          const { from, to } = row?.original || {};

          return (
            <div className={styles.source}>
              <span>{from || '--'}</span>
              ➡️
              <span>{to || '--'}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'status',
        header: fm('status'),
        cell: ({ row }) => {
          const { status } = row?.original || {};

          return <BridgeStatusTag status={status} />;
        },
      },
    ];
  }, [fm, pageNo]);

  const awaitDataCols = useMemo(() => {
    return [
      {
        accessorKey: 'rank',
        header: fm('rank'),
        cell: ({ row }) => {
          const { index } = row?.original || {};

          return (
            <div className={styles.rank}>
              <AwaitRankLogo />
              <span>{index + 1}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'name',
        header: fm('endPoint'),
        cell: ({ row }) => {
          const { name, logo } = row?.original || {};

          return (
            <div className={styles.endPoint}>
              <Avatar src={logo} size="24" name={name} />
              <div className={styles.name}>{name}</div>
            </div>
          );
        },
      },
      {
        accessorKey: 'unblocked',
        header: fm('fundAwaitResponse'),
        cell: ({ row }) => {
          const { unblocked } = row?.original || {};
          if (!unblocked) return '--';
          return (
            <div className={styles.unblocked}>{`$${formatNumber(unblocked, {
              kmb: false,
              decimal: 0,
              noTrailZero: true,
              roundMode: 1,
            })}`}</div>
          );
        },
      },
      {
        accessorKey: 'lostFund',
        header: fm('lostFunds'),
        cell: ({ row }) => {
          const { lostFund } = row?.original || {};
          if (!lostFund) return '--';
          return (
            <div className={styles.lostFund}>{`$${formatNumber(lostFund, {
              kmb: false,
              decimal: 0,
              noTrailZero: true,
              roundMode: 1,
            })}`}</div>
          );
        },
      },
      {
        accessorKey: 'supportStatus',
        header: fm('status'),
        cell: ({ row }) => {
          const { supportStatus } = row?.original || {};

          return <StatusTag status={supportStatus} />;
        },
      },
      {
        accessorKey: 'awaitingResponseTime',
        header: fm('nonResponseTime'),
        cell: ({ row }) => {
          const { awaitingResponseTime } = row?.original || {};

          return (
            <div className={styles.awaitingResponsetime}>
              {/* {formatTimeDiff(awaitingResponseTime)}
               */}
              {awaitingResponseTime || '--'}
            </div>
          );
        },
      },
      {
        accessorKey: 'reportedAddressCount',
        header: fm('reportsPendingAction'),
        cell: ({ row }) => {
          const { reportedAddressCount, bannedAddressCount } =
            row?.original || {};

          const count = BigNumber(reportedAddressCount)
            .minus(bannedAddressCount)
            .toNumber();
          return count || '--';
        },
      },
      {
        header: fm('action'),
        cell: ({ row }) => {
          const { name } = row?.original || {};

          return (
            <div
              className={styles.action}
              onClick={() => {
                scTrack(Event.HackbountyUserClick, {
                  page_name: Page.HackbountyHomePage,
                  button_name: 'ViewMore',
                });

                router.push(`/${locale}${appPath}/detail/?entity=${name}`);
              }}
            >
              <span>{fm('viewMore')}</span>
              <ArrowchevronRightIcon className={styles.arrow} />
            </div>
          );
        },
      },
    ];
  }, [fm, locale, router]);

  const respondedDataCols = useMemo(() => {
    return [
      {
        accessorKey: 'rank',
        header: fm('rank'),
        cell: ({ row }) => {
          const { index } = row?.original || {};

          return (
            <div className={styles.rank}>
              <RespondedRankLogo />
              <span>{index + 1}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'name',
        header: fm('endPoint'),
        cell: ({ row }) => {
          const { name, logo } = row?.original || {};

          return (
            <div className={styles.endPoint}>
              <Avatar src={logo} size="24" name={name} />
              <div className={styles.name}>{name}</div>
            </div>
          );
        },
      },
      {
        accessorKey: 'received',
        header: fm('respondedFund'),
        cell: ({ row }) => {
          const { received } = row?.original || {};
          if (!received) return '--';
          return (
            <div className={styles.unblocked}>{`$${formatNumber(received, {
              kmb: false,
              decimal: 0,
              noTrailZero: true,
              roundMode: 1,
            })}`}</div>
          );
        },
      },
      {
        accessorKey: 'blocked',
        header: fm('blockedFunds'),
        cell: ({ row }) => {
          const { blocked } = row?.original || {};
          if (!blocked) return '--';
          return (
            <div className={styles.lostFund}>{`$${formatNumber(blocked, {
              kmb: false,
              decimal: 0,
              noTrailZero: true,
              roundMode: 1,
            })}`}</div>
          );
        },
      },
      {
        accessorKey: 'lostFund',
        header: fm('lostFunds'),
        cell: ({ row }) => {
          const { lostFund } = row?.original || {};
          if (!lostFund) return '--';
          return (
            <div className={styles.lostFund}>{`$${formatNumber(lostFund, {
              kmb: false,
              decimal: 0,
              noTrailZero: true,
              roundMode: 1,
            })}`}</div>
          );
        },
      },
      {
        accessorKey: 'supportStatus',
        header: fm('status'),
        cell: ({ row }) => {
          const { supportStatus } = row?.original || {};

          return <StatusTag status={supportStatus} />;
        },
      },
      {
        header: fm('action'),
        cell: ({ row }) => {
          const { name } = row?.original || {};

          return (
            <div
              className={styles.action}
              onClick={() => {
                router.push(`/${locale}${appPath}/detail/?entity=${name}`);
              }}
            >
              <span>{fm('viewMore')}</span>
              <ArrowchevronRightIcon className={styles.arrow} />
            </div>
          );
        },
      },
    ];
  }, [fm, locale, router]);

  const cols = useMemo(() => {
    if (currentTab === TabKey.BRIDGE) {
      return bridgeCols;
    }
    if (currentTab === TabKey.AWAIT_RESPONSE) {
      return awaitDataCols;
    }
    return respondedDataCols;
  }, [currentTab, respondedDataCols, bridgeCols, awaitDataCols]);

  return {
    cols,
  };
};
